'use client';

/**
 * ComputedRenderer Component
 *
 * Renders CALCULATED, ROLLUP, FORMULA, and AUTO_NUMBER field types.
 * All computed fields are read-only (display-only, no edit mode).
 */

import CalculateIcon from '@mui/icons-material/Calculate';
import FunctionsIcon from '@mui/icons-material/Functions';
import SummarizeIcon from '@mui/icons-material/Summarize';
import TagIcon from '@mui/icons-material/Tag';
import Box from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { getNeonStyles, type NeonAccent } from '../../../theme/palette';
import type { FieldDefinition, FieldMode } from '../../../types/entityFramework';
import { formatCurrency, formatNumber } from '../../../utils/formatters';
import { GlassTooltip } from '../../GlassTooltip';

// ============================================================================
// Types
// ============================================================================

export interface ComputedRendererProps {
  /** Field definition */
  field: FieldDefinition;
  /** Current value */
  value: unknown;
  /** Render mode (always renders as display, ignores edit) */
  mode: FieldMode;
  /** Change handler (ignored for computed fields) */
  onChange?: (value: unknown) => void;
  /** Error message */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
  /** Neon accent color */
  neonAccent?: NeonAccent;
  /** Additional class name */
  className?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

interface ComputedContainerProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
  $size: 'small' | 'medium' | 'large';
}

const ComputedContainer = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<ComputedContainerProps>(({ $isDark, $neonAccent, $size }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  const sizeStyles = {
    small: { fontSize: '0.875rem', padding: '4px 8px', gap: 4 },
    medium: { fontSize: '1rem', padding: '8px 12px', gap: 6 },
    large: { fontSize: '1.125rem', padding: '12px 16px', gap: 8 },
  };

  const s = sizeStyles[$size];

  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: s.gap,
    padding: s.padding,
    fontSize: s.fontSize,
    borderRadius: 8,
    backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
    border: `1px solid ${
      neonStyles ? neonStyles.border : $isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'
    }`,
  };
});

const DisplayValue = styled(Typography, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean }>(({ $isDark }) => ({
  color: $isDark ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.87)',
  fontFamily: 'monospace',
  fontWeight: 500,
}));

interface IconWrapperProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}

const IconWrapper = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<IconWrapperProps>(({ $isDark, $neonAccent }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: neonStyles ? neonStyles.base : $isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)',
    '& svg': {
      fontSize: '1.1em',
    },
  };
});

const EmptyValue = styled(Typography)({
  color: 'inherit',
  opacity: 0.5,
  fontStyle: 'italic',
});

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get the appropriate icon for the computed field type
 */
function getComputedIcon(fieldType: string): React.ReactElement {
  switch (fieldType) {
    case 'CALCULATED':
      return <CalculateIcon />;
    case 'FORMULA':
      return <FunctionsIcon />;
    case 'ROLLUP':
      return <SummarizeIcon />;
    case 'AUTO_NUMBER':
      return <TagIcon />;
    default:
      return <CalculateIcon />;
  }
}

/**
 * Get the rollup type label for tooltip
 */
function getRollupTypeLabel(formula: string | undefined): string {
  if (!formula) return 'Aggregated value';

  const upperFormula = formula.toUpperCase();
  if (upperFormula.includes('SUM')) return 'Sum';
  if (upperFormula.includes('COUNT')) return 'Count';
  if (upperFormula.includes('AVG') || upperFormula.includes('AVERAGE')) return 'Average';
  if (upperFormula.includes('MIN')) return 'Minimum';
  if (upperFormula.includes('MAX')) return 'Maximum';
  if (upperFormula.includes('FIRST')) return 'First value';
  if (upperFormula.includes('LAST')) return 'Last value';

  return 'Aggregated value';
}

/**
 * Format the computed value based on field type and value type
 */
function formatComputedValue(value: unknown, field: FieldDefinition): string {
  if (value == null) return '';

  // Handle AUTO_NUMBER - always show as string with prefix
  if (field.fieldType === 'AUTO_NUMBER') {
    const prefix = field.autoNumberPrefix || '';
    return `${prefix}${value}`;
  }

  // Handle numbers
  if (typeof value === 'number') {
    // Check if field has currency formatting
    if (field.currency) {
      return formatCurrency(value, field.currency);
    }

    // Apply precision if specified
    const options: Intl.NumberFormatOptions = {};
    if (field.precision != null) {
      options.minimumFractionDigits = field.precision;
      options.maximumFractionDigits = field.precision;
    }

    return formatNumber(value, options);
  }

  // Handle strings and other types
  return String(value);
}

/**
 * Build tooltip content for computed field
 */
function getTooltipContent(field: FieldDefinition): string {
  const parts: string[] = [];

  switch (field.fieldType) {
    case 'CALCULATED':
      parts.push('Calculated field');
      if (field.formula) {
        parts.push(`Formula: ${field.formula}`);
      }
      break;
    case 'FORMULA':
      parts.push('Formula field');
      if (field.formula) {
        parts.push(`Expression: ${field.formula}`);
      }
      break;
    case 'ROLLUP':
      parts.push(`Rollup: ${getRollupTypeLabel(field.formula)}`);
      if (field.formula) {
        parts.push(`Source: ${field.formula}`);
      }
      break;
    case 'AUTO_NUMBER':
      parts.push('Auto-generated number');
      if (field.autoNumberPrefix) {
        parts.push(`Prefix: ${field.autoNumberPrefix}`);
      }
      break;
  }

  return parts.join('\n');
}

// ============================================================================
// Component
// ============================================================================

export const ComputedRenderer: React.FC<ComputedRendererProps> = ({
  field,
  value,
  // Computed fields are always display-only, ignoring mode/onChange/error/disabled
  mode: _mode,
  onChange: _onChange,
  error: _error,
  disabled: _disabled = false,
  size = 'medium',
  neonAccent,
  className,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const formattedValue = formatComputedValue(value, field);
  const tooltipContent = getTooltipContent(field);
  const icon = getComputedIcon(field.fieldType);

  // Empty state
  if (!formattedValue) {
    return (
      <Box className={className}>
        <EmptyValue variant="body2">-</EmptyValue>
      </Box>
    );
  }

  return (
    <GlassTooltip title={tooltipContent} arrow placement="top">
      <ComputedContainer $isDark={isDark} $neonAccent={neonAccent} $size={size} className={className}>
        <IconWrapper $isDark={isDark} $neonAccent={neonAccent}>
          {icon}
        </IconWrapper>
        <DisplayValue variant="body1" $isDark={isDark}>
          {formattedValue}
        </DisplayValue>
      </ComputedContainer>
    </GlassTooltip>
  );
};

ComputedRenderer.displayName = 'ComputedRenderer';

export default ComputedRenderer;
